import { apiUrl } from "../api/endpoints";
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../Components/modal.css';
import './edit.css';

import Alert from '../Components/Alert';
import { LanguageContext } from '../context/LanguageContext';
import dashboardTranslations from '../languages/dashboard.json';
import { getErrorMessage } from '../utils/errorUtils';
import { useAuth } from '../hooks/AuthProvider';
import { useFontSize } from '../context/FontSizeContext';
import { TemplateIcon } from '../svg/TemplateIcons';
import { NameIcon, AddNewIcon } from '../svg/UtilsIcons';

export const AddDashboardExample = ({ setOpen, refresh }) => {
    const { permissions } = useAuth();
    const canManageDashboardExamples = permissions['add_new_dashoard'];
    const { lang } = useContext(LanguageContext);
    useFontSize();
    const navigate = useNavigate();

    const dashboardPageT = dashboardTranslations[lang] || dashboardTranslations.en || {};
    const addDashboardT = dashboardPageT.addDashboard || {};
    const isRtl = ['ar', 'ur'].includes(lang);

    const copy = {
        title: addDashboardT.exampleTitle || 'Create Dashboard Example',
        description:
            addDashboardT.exampleDescription ||
            'Create a dashboard example, then open its details page to edit tabs, widgets, and icon settings.',
        labelName: addDashboardT.exampleNameLabel || 'Name',
        labelExampleName: addDashboardT.exampleLibraryNameLabel || 'Example Name',
        placeholderName: addDashboardT.exampleNamePlaceholder || 'Dashboard example name',
        placeholderExampleName:
            addDashboardT.exampleLibraryNamePlaceholder || 'Library example name',
        buttonCancel: addDashboardT.buttonCancel || 'Cancel',
        buttonDone: addDashboardT.exampleButtonDone || 'Create',
        permissionTitle: addDashboardT.permissionDenied?.title || 'Permission Denied',
        permissionMessage:
            addDashboardT.permissionDenied?.dashboardExampleMessage ||
            'You do not have permission to manage dashboard examples.',
    };

    const [formData, setFormData] = useState({
        name: '',
        example_name: '',
    });
    const [submitting, setSubmitting] = useState(false);

    const [alertState, setAlertState] = useState({
        open: false,
        type: 'success',
        message: '',
        position: isRtl ? 'left' : 'right',
    });

    const showAlert = (type, message) => {
        setAlertState({
            open: true,
            type,
            message,
            position: isRtl ? 'left' : 'right',
        });
    };

    const handleAlertClose = () => {
        setAlertState((prev) => ({ ...prev, open: false }));
    };

    const onChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const createDashboardExample = async () => {
        if (submitting) return;

        setSubmitting(true);
        try {
            const payload = {
                name: String(formData.name || '').trim(),
                example_name: String(formData.example_name || '').trim(),
            };

            const res = await axios.post(
                apiUrl(`dashboard/examples/create`),
                payload,
                { headers: { 'Content-Type': 'application/json' } }
            );

            if (res.status !== 201 || res.data?.status !== 'OK') {
                throw new Error(addDashboardT.unexpectedResponse || 'Received an unexpected response from the server.');
            }

            const createdId =
                res.data?.data?.dashboard_example_id ??
                res.data?.data?.id;

            showAlert(
                'success',
                res.data?.message || addDashboardT.exampleCreateSuccess || 'Dashboard example created successfully.'
            );

            refresh?.();

            if (createdId) {
                setTimeout(() => {
                    setOpen(false);
                    navigate(`/configuration/dashboard-examples/details/${createdId}`);
                }, 600);
            } else {
                setTimeout(() => setOpen(false), 1200);
            }
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            showAlert('error', errorMessage);
            setSubmitting(false);
        }
    };

    if (!canManageDashboardExamples) {
        return (
            <div id="modal" style={{ direction: isRtl ? 'rtl' : 'ltr' }}>
                <div
                    id="popup"
                    className="dashboard-modal"
                    style={{
                        animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
                        fontSize:
                            'calc(var(--body-font-size) * var(--font-size-multiplier))',
                    }}
                >
                    <div className="dashboard-header">
                        <div className="dashboard-header-icon">
                            <AddNewIcon />
                        </div>
                        <div className="dashboard-header-text">
                            <h2>{copy.permissionTitle}</h2>
                            <p>{copy.permissionMessage}</p>
                        </div>
                    </div>

                    <div className="modal-divider" />

                    <div className="modal-content dashboard-content" />

                    <div className="modal-footer dashboard-footer">
                        <button
                            className="btn btn-cancel"
                            onClick={() => setOpen(false)}
                        >
                            {copy.buttonCancel}
                        </button>
                        <div />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div id="modal" style={{ direction: isRtl ? 'rtl' : 'ltr' }}>
            <Alert
                type={alertState.type}
                position={alertState.position}
                message={alertState.message}
                open={alertState.open}
                close={handleAlertClose}
            />

            <div
                id="popup"
                className="dashboard-modal"
                style={{
                    animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
                    fontSize:
                        'calc(var(--body-font-size) * var(--font-size-multiplier))',
                }}
            >
                <div className="dashboard-header">
                    <div className="dashboard-header-icon">
                        <TemplateIcon />
                    </div>
                    <div className="dashboard-header-text">
                        <h2>{copy.title}</h2>
                        <p>{copy.description}</p>
                    </div>
                </div>

                <div className="modal-divider" />

                <div className="modal-content dashboard-content">
                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-icon">
                                <NameIcon />
                            </span>
                            <span className="form-section-title-text">
                                {copy.labelName}
                            </span>
                        </div>

                        <div className="form-item">
                            <div className="field-with-counter">
                                <input
                                    name="name"
                                    type="text"
                                    className="add-column"
                                    placeholder={copy.placeholderName}
                                    maxLength={100}
                                    value={formData.name}
                                    onChange={onChange}
                                />
                                <span className="field-counter">
                                    {formData.name.length} / 100
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-icon">
                                <TemplateIcon />
                            </span>
                            <span className="form-section-title-text">
                                {copy.labelExampleName}
                            </span>
                        </div>

                        <div className="form-item">
                            <div className="field-with-counter">
                                <input
                                    name="example_name"
                                    type="text"
                                    className="add-column"
                                    placeholder={copy.placeholderExampleName}
                                    maxLength={100}
                                    value={formData.example_name}
                                    onChange={onChange}
                                />
                                <span className="field-counter">
                                    {formData.example_name.length} / 100
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="modal-footer dashboard-footer">
                    <button
                        className="btn btn-cancel"
                        onClick={() => setOpen(false)}
                        disabled={submitting}
                    >
                        {copy.buttonCancel}
                    </button>
                    <button
                        className="btn btn-apply dashboard-primary-btn"
                        onClick={createDashboardExample}
                        disabled={submitting}
                    >
                        {copy.buttonDone}
                    </button>
                </div>
            </div>
        </div>
    );
};
